(() => {
  const SOLID_CLASS = 'moon-header-solid';
  const RICH_OVERLAP_CLASS = 'moon-header-rich-overlap';
  const LIGHT_THRESHOLD = 155;
  const RICH_SELECTORS = [
    '.banner',
    '.hero-wrapper',
    '.slideshow',
    '[class*="hero"]',
    '[class*="banner"]'
  ];
  const imageSamples = new WeakMap();

  const parseRgb = (value) => {
    const match = value && value.match(/rgba?\(([^)]+)\)/);
    if (!match) return null;
    const parts = match[1].split(',').map((part) => Number.parseFloat(part.trim()));
    if (parts.length < 3 || parts.some((part, index) => index < 3 && Number.isNaN(part))) return null;
    const alpha = parts.length > 3 ? parts[3] : 1;
    if (alpha < 0.35) return null;
    return parts.slice(0, 3);
  };

  const luminance = ([r, g, b]) => (0.2126 * r + 0.7152 * g + 0.0722 * b);

  const readableImageSample = (image, viewportX, viewportY) => {
    if (!image || !image.complete || !image.naturalWidth || !image.naturalHeight) return null;

    const rect = image.getBoundingClientRect();
    if (
      viewportX < rect.left ||
      viewportX > rect.right ||
      viewportY < rect.top ||
      viewportY > rect.bottom ||
      rect.width <= 0 ||
      rect.height <= 0
    ) {
      return null;
    }

    let sample = imageSamples.get(image);
    if (!sample || sample.width !== image.naturalWidth || sample.height !== image.naturalHeight) {
      const canvas = document.createElement('canvas');
      canvas.width = image.naturalWidth;
      canvas.height = image.naturalHeight;
      const context = canvas.getContext('2d', { willReadFrequently: true });

      try {
        context.drawImage(image, 0, 0, canvas.width, canvas.height);
        sample = { canvas, context, width: canvas.width, height: canvas.height };
        imageSamples.set(image, sample);
      } catch (_error) {
        return null;
      }
    }

    const objectFit = window.getComputedStyle(image).objectFit;
    const renderedRatio = rect.width / rect.height;
    const naturalRatio = image.naturalWidth / image.naturalHeight;
    let sourceWidth = image.naturalWidth;
    let sourceHeight = image.naturalHeight;
    let sourceX = 0;
    let sourceY = 0;

    if (objectFit === 'cover') {
      if (naturalRatio > renderedRatio) {
        sourceWidth = image.naturalHeight * renderedRatio;
        sourceX = (image.naturalWidth - sourceWidth) / 2;
      } else {
        sourceHeight = image.naturalWidth / renderedRatio;
        sourceY = (image.naturalHeight - sourceHeight) / 2;
      }
    }

    const normalizedX = (viewportX - rect.left) / rect.width;
    const normalizedY = (viewportY - rect.top) / rect.height;
    const pixelX = Math.max(0, Math.min(image.naturalWidth - 1, Math.round(sourceX + normalizedX * sourceWidth)));
    const pixelY = Math.max(0, Math.min(image.naturalHeight - 1, Math.round(sourceY + normalizedY * sourceHeight)));

    try {
      return [...sample.context.getImageData(pixelX, pixelY, 1, 1).data].slice(0, 3);
    } catch (_error) {
      return null;
    }
  };

  const bannerPixelAt = (x, y) => {
    const banner = document.elementFromPoint(x, y)?.closest(RICH_SELECTORS.join(','));
    const images = banner ? [...banner.querySelectorAll('img')] : [];

    for (const image of images) {
      const pixel = readableImageSample(image, x, y);
      if (pixel) return pixel;
    }

    return null;
  };

  const visibleBackdropElement = (x, y, header) => {
    return document.elementsFromPoint(x, y).find((element) => {
      if (!element || element === document.documentElement || element === document.body) return false;
      if (header && (element === header || header.contains(element))) return false;
      if (element.closest('.announcement-bar-section')) return false;
      return true;
    });
  };

  const overlapsRichBanner = (header) => {
    const rect = header.getBoundingClientRect();
    const overlapThreshold = rect.height * 0.35;

    return [...document.querySelectorAll(RICH_SELECTORS.join(','))].some((element) => {
      const bannerRect = element.getBoundingClientRect();
      if (bannerRect.width <= 0 || bannerRect.height <= 0) return false;
      const overlap = Math.min(rect.bottom, bannerRect.bottom) - Math.max(rect.top, bannerRect.top);
      return overlap >= overlapThreshold;
    });
  };

  const shouldUseSolidHeader = () => {
    const header = document.querySelector('.section-header');
    if (!header) return false;
    if (overlapsRichBanner(header)) return false;

    const rect = header.getBoundingClientRect();
    const sampleY = Math.max(1, Math.min(window.innerHeight - 1, rect.top + rect.height * 0.52));
    const sampleXs = [0.18, 0.34, 0.5, 0.66, 0.82].map((ratio) => Math.round(window.innerWidth * ratio));

    let lightVotes = 0;
    let richVotes = 0;

    sampleXs.forEach((x) => {
      const pixel = bannerPixelAt(x, sampleY);
      if (pixel) {
        if (luminance(pixel) > LIGHT_THRESHOLD) lightVotes += 1;
        else richVotes += 1;
        return;
      }

      const element = visibleBackdropElement(x, sampleY, header);
      if (!element) {
        lightVotes += 1;
        return;
      }

      if (element.closest(RICH_SELECTORS.join(','))) {
        richVotes += 1;
        return;
      }

      let current = element;
      while (current && current !== document.documentElement) {
        const style = window.getComputedStyle(current);
        const rgb = parseRgb(style.backgroundColor);
        if (rgb) {
          if (luminance(rgb) > LIGHT_THRESHOLD) lightVotes += 1;
          else richVotes += 1;
          return;
        }
        current = current.parentElement;
      }

      lightVotes += 1;
    });

    return lightVotes > richVotes;
  };

  let ticking = false;

  const updateHeaderContrast = () => {
    ticking = false;
    const header = document.querySelector('.section-header');
    const richOverlap = header ? overlapsRichBanner(header) : false;
    document.body.classList.toggle(RICH_OVERLAP_CLASS, richOverlap);
    document.body.classList.toggle(SOLID_CLASS, !richOverlap && shouldUseSolidHeader());
  };

  const requestUpdate = () => {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(updateHeaderContrast);
  };

  document.addEventListener('DOMContentLoaded', updateHeaderContrast);
  window.addEventListener('load', updateHeaderContrast);
  window.addEventListener('scroll', requestUpdate, { passive: true });
  window.addEventListener('resize', requestUpdate);
})();
